import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import {
  downloadCrusherReportPdf,
  downloadIotDeviceReportPdf,
} from "./UrjabioPdfgen.jsx";
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, Layers, ChevronLeft, ChevronRight } from "lucide-react";

const ALL_SENSOR_COLORS = [
  "#16a34a", "#0284c7", "#d97706", "#9333ea",
  "#dc2626", "#0891b2", "#c026d3", "#ea580c",
  "#4f46e5", "#059669", "#be185d", "#ca8a04",
];

const BASE = "https://api.livestockify.com/api/urjabio";
const IOT_BASE = "https://api.livestockify.com/api/iot";

/** Native <input type="date"> — Safari-friendly: restores UA chrome, light scheme, visible calendar icon. */
function NativeDateInput({ id, value, onChange, "aria-label": ariaLabel, className = "" }) {
  return (
    <div className={`relative min-w-0 ${className}`}>
      <input
        id={id}
        type="date"
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
        className="w-full min-h-10 min-w-0 cursor-pointer rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-10 text-sm text-slate-900 shadow-none outline-none appearance-auto [color-scheme:light] focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 sm:min-w-[11rem] [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-y-0 [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:z-[1] [&::-webkit-calendar-picker-indicator]:w-10 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
      />
      <Calendar
        className="pointer-events-none absolute right-2.5 top-1/2 z-0 h-4 w-4 -translate-y-1/2 text-slate-500"
        strokeWidth={2}
        aria-hidden
      />
    </div>
  );
}

const MetricsPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceDataMap, setDeviceDataMap] = useState({});
  const [crusherTotalsMap, setCrusherTotalsMap] = useState({});
  const [selectedMetric, setSelectedMetric] = useState(null);
  const [showGraph, setShowGraph] = useState(false);
  const [showValueBoxes, setShowValueBoxes] = useState(false);
  const [chartData, setChartData] = useState([]);
  // dwonload popup code here
  const [showDownloadPopup, setShowDownloadPopup] = useState(false);
  const [rangeType, setRangeType] = useState("Today");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [selectedRange, setSelectedRange] = useState("Daily");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [showAllSensorsGraph, setShowAllSensorsGraph] = useState(false);
  const [allSensorsChartData, setAllSensorsChartData] = useState([]);
  const [allSensorKeys, setAllSensorKeys] = useState([]);
  const [allSensorsRange, setAllSensorsRange] = useState("Daily");
  const [allSensorsLoading, setAllSensorsLoading] = useState(false);

  // ✅ Waste Crusher States
  const [totalWasteToday, setTotalWasteToday] = useState(0);
  const [crusherRangeTotals, setCrusherRangeTotals] = useState({
    totalCrush: 0,
    totalOnTimeMinutes: 0,
  });

  const chartRef = useRef(null);
  const sensorScrollRef = useRef(null);
  const navigate = useNavigate();

  const scrollSensors = (direction) => {
    sensorScrollRef.current?.scrollBy({
      left: direction === "left" ? -280 : 280,
      behavior: "smooth",
    });
  };

  /** Horizontal chart panning only on narrow viewports (phones); tablet / laptop use full width. */
  const [chartHScrollEnabled, setChartHScrollEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 767px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 767px)");
    const onChange = () => setChartHScrollEnabled(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const isCrusherDeviceCheck = (device) => {
    if (!device) return false;

    const id = String(device.deviceId || "").toLowerCase();
    const name = String(device.name || "").toLowerCase();

    return id.startsWith("20") && name.includes("crusher");
  };

  const formatCardSensorValue = (val) => {
    if (val === null || val === undefined || val === "-") return "-";
    const num = parseFloat(val);
    if (Number.isNaN(num)) return String(val);
    return num.toFixed(3);
  };

  const formatCardTimestamp = (ts) => {
    if (!ts) return null;
    try {
      return new Date(ts).toLocaleString();
    } catch {
      return null;
    }
  };

  const mapRangeToPeriod = (range) => {
    if (range === "Daily") return "today";
    if (range === "Weekly") return "week";
    if (range === "Monthly") return "month";
    if (range === "Custom") return "custom";
    return "today";
  };

  const fetchLatestDeviceData = async (deviceId) => {
    if (!deviceId || !token) return null;
    try {
      const res = await axios.get(`${IOT_BASE}/data/${deviceId}/latest`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const latestData = Array.isArray(res.data) ? res.data[0] : res.data;
      const sensors = { ...(latestData?.sensors || {}) };
      if (latestData?.timestamp && sensors.timestamp == null) {
        sensors.timestamp = latestData.timestamp;
      }
      return sensors;
    } catch {
      return null;
    }
  };

  const fetchGraphByRange = async (
    range,
    customStartValue = null,
    customEndValue = null,
  ) => {
    if (!selectedMetric || selectedMetric === "crusher" || !selectedDevice || !token) {
      return;
    }

    const period = mapRangeToPeriod(range);
    const params = { period };
    if (period === "custom") {
      if (!customStartValue || !customEndValue) return;
      params.st = customStartValue;
      params.et = customEndValue;
    }

    try {
      const res = await axios.get(
        `${IOT_BASE}/data/period/${selectedDevice.deviceId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );

      const raw = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
          ? res.data.data
          : [];

      const formatted = raw
        .map((item) => {
          const ts = item.timestamp || item.createdAt || item.time;
          const dateObj = new Date(ts);
          if (!Number.isFinite(dateObj.getTime())) return null;
          const sensors = item.sensors || {};
          const value = sensors[selectedMetric] ?? item[selectedMetric] ?? null;
          return {
            day: dateObj.toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
            fullDate: dateObj.toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            }),
            fullTime: dateObj.getTime(),
            value: typeof value === "number" ? value : Number(value),
          };
        })
        .filter(Boolean)
        .filter((item) => Number.isFinite(item.value))
        .sort((a, b) => a.fullTime - b.fullTime);

      setChartData(formatted);
      setShowGraph(true);
    } catch (error) {
      console.error("Error fetching period graph data:", error);
      setChartData([]);
    }
  };
  const fetchCrusherTotal = async () => {};
  const fetchCrusherTotalForDevice = async () => {};
  const fetchCrusherSummary = async () => {};

  const fetchAllSensorsGraph = async (range = "Daily") => {
    if (!selectedDevice || !token) return;
    setAllSensorsLoading(true);
    try {
      const period = mapRangeToPeriod(range);
      const res = await axios.get(
        `${IOT_BASE}/data/period/${selectedDevice.deviceId}`,
        { headers: { Authorization: `Bearer ${token}` }, params: { period } },
      );
      const raw = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data) ? res.data.data : [];

      const keysSet = new Set();
      const formatted = raw
        .map((item) => {
          const ts = item.timestamp || item.createdAt || item.time;
          const dateObj = new Date(ts);
          if (!Number.isFinite(dateObj.getTime())) return null;
          const sensors = item.sensors || {};
          const row = {
            fullTime: dateObj.getTime(),
            fullDate: dateObj.toLocaleString("en-GB", {
              day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
            }),
          };
          Object.entries(sensors).forEach(([key, val]) => {
            if (key.toLowerCase() === "timestamp") return;
            const num = Number(val);
            if (Number.isFinite(num)) {
              row[key] = num;
              keysSet.add(key);
            }
          });
          return row;
        })
        .filter(Boolean)
        .sort((a, b) => a.fullTime - b.fullTime);

      setAllSensorKeys([...keysSet]);
      setAllSensorsChartData(formatted);
      setAllSensorsRange(range);
      setShowAllSensorsGraph(true);
      setShowGraph(false);
      setSelectedMetric(null);
    } catch (err) {
      console.error("Error fetching all sensors:", err);
    } finally {
      setAllSensorsLoading(false);
    }
  };

  const token = localStorage.getItem("token");

  const fetchDevices = async (uuid) => {
    try {
      const res = await axios.get(`${BASE}/projects/devices/${uuid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.devices && Array.isArray(res.data.devices)) {
        const deviceList = res.data.devices;
        setDevices(deviceList);
        const readings = await Promise.all(
          deviceList.map(async (device) => {
            const sensors = await fetchLatestDeviceData(device.deviceId);
            return [device.deviceId, sensors || {}];
          }),
        );
        setDeviceDataMap((prev) => ({
          ...prev,
          ...Object.fromEntries(readings),
        }));

        if (!selectedDevice) {
          setSelectedDevice(deviceList[0]);
        }
      } else {
        setDevices([]);
      }
    } catch (err) {
      console.error("Error fetching devices:", err);
      setDevices([]);
    }
  };

  // ✅ Step 1: Fetch Project Details (if needed)
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const res = await axios.get(`${BASE}/projects/uuid/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?.projectUuid) {
          setProject(res.data);
          fetchDevices(res.data.projectUuid);
        }
      } catch (err) {
        console.error("Error fetching project:", err);
      }
    };

    fetchProject();
  }, [projectId, token]);

  useEffect(() => {
    if (!selectedDevice) return;
    if (selectedMetric && selectedRange !== "Custom") {
      fetchGraphByRange(selectedRange);
    }
  }, [selectedDevice, selectedMetric, selectedRange]);

  useEffect(() => {
    if (!selectedDevice) return;
    setShowValueBoxes(true);
    setShowGraph(false);
    setShowAllSensorsGraph(false);
    setSelectedMetric(null);
    setChartData([]);
    setAllSensorsChartData([]);
  }, [selectedDevice]);

  const handleDeviceClick = (device) => {
    setSelectedDevice(device);
    setShowValueBoxes(true);
  };

  // 🔹 Handle Metric click
  const handleMetricClick = (metricName) => {
    setShowAllSensorsGraph(false);
    setSelectedMetric(metricName);
    setSelectedRange("Daily");
    fetchGraphByRange("Daily");
  };

  const handleDownloadPDF = () =>
    downloadIotDeviceReportPdf({
      token,
      device: selectedDevice,
      rangeType,
      startDate,
      endDate,
    });

  const handleCrusherDownloadPDF = () =>
    downloadCrusherReportPdf({
      device: selectedDevice,
      selectedRange,
      customStart,
      customEnd,
      crusherRangeTotals,
    });

  const chartScrollWidthPx = useMemo(() => {
    if (!chartData?.length) return null;
    return Math.min(Math.max(chartData.length * 12 + 100, 320), 2800);
  }, [chartData]);

  const chartDotR = useMemo(() => {
    const n = chartData?.length ?? 0;
    if (n > 80) return 1.25;
    if (n > 40) return 1.75;
    return 2.5;
  }, [chartData]);

  /** Unique timestamps on X — avoids duplicate `day` labels stacking points and trapping the tooltip. */
  const formatChartAxisTick = useMemo(() => {
    return (timestamp) => {
      const d = new Date(timestamp);
      if (!Number.isFinite(d.getTime())) return "";
      if (selectedRange === "Monthly") {
        return d.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        });
      }
      if (selectedRange === "Weekly") {
        return d.toLocaleDateString("en-IN", {
          weekday: "short",
          day: "2-digit",
        });
      }
      return d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    };
  }, [selectedRange]);

  const [, setChartScrollTick] = useState(0);

  useEffect(() => {
    const el = chartRef.current;
    if (!el || !showGraph) return;
    const onScroll = () => setChartScrollTick((t) => t + 1);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [showGraph, chartData.length]);

  return (
    <div className="min-h-screen w-full min-w-0 text-slate-900">
      <div className="flex w-full flex-col gap-4 sm:gap-5 lg:gap-6">
      <div className="text-sm text-black-600 flex items-center gap-2">
        {/* Dashboard Link */}
        <button
          onClick={() => navigate("/urjabio/admin/dashboard")}
          className="font-semibold text-xl"
        >
          Dashboard
        </button>

        <span className="text-gray-400">›</span>

        <span className="font-semibold text-gray-800 text-xl">
          Project Details
        </span>
      </div>

      {/* 🔹 Project Header */}
      {project && (
        <div>
          <h2 className="text-2xl text-left sm:text-3xl font-bold text-[#22592A]">
            {project.projectName}
          </h2>
          <p className="text-black text-xl text-left">
            {project.projectLocation}
          </p>
        </div>
      )}

      {/* 🔹 Device Cards */}
      <div className="flex flex-wrap justify-start gap-4 sm:gap-5">
        {devices.length > 0 ? (
          devices.map((device) => {
            const live = deviceDataMap[device.deviceId];
            const lastUpdated = formatCardTimestamp(live?.timestamp);
            const sensorEntries = live
              ? Object.entries(live).filter(
                  ([key]) => key.toLowerCase() !== "timestamp",
                )
              : [];
            const hasSensorRows = sensorEntries.length > 0;

            return (
              <div
                key={device.deviceUuid}
                onClick={() => handleDeviceClick(device)}
                className={`group flex w-full max-w-[248px] shrink-0 cursor-pointer flex-col overflow-hidden rounded-2xl bg-gradient-to-br from-[#2ec155] via-[#34C759] to-emerald-700 shadow-lg shadow-emerald-900/20 ring-1 ring-white/25 transition duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-900/25 sm:w-[248px] ${
                  selectedDevice?.deviceUuid === device.deviceUuid
                    ? "ring-2 ring-emerald-500 ring-offset-2 ring-offset-white"
                    : ""
                }`}
              >
                <div className="flex items-start justify-between gap-2 px-4 py-3.5 text-white sm:px-5 sm:py-4">
                  <div className="min-w-0 flex flex-col items-start text-left">
                    <h3 className="text-base font-semibold leading-snug tracking-tight sm:text-lg">
                      {device.name || "Device"}
                    </h3>
                    <p className="mt-0.5 text-sm text-white/85">
                      Device ID: {device.deviceId}
                    </p>
                    {lastUpdated && (
                      <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-white/70">
                        Updated {lastUpdated}
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-auto px-3 pb-3 sm:px-4 sm:pb-4">
                  <div className="flex h-40 flex-col rounded-xl border border-white/40 bg-white/95 p-3 text-slate-800 shadow-inner backdrop-blur-sm sm:h-44 sm:p-4">
                    {device.name && (
                      <div className="mb-2 border-b border-slate-100 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 sm:text-sm sm:normal-case sm:tracking-normal">
                        {device.name}
                      </div>
                    )}

                    <div className="grid flex-1 grid-cols-2 gap-1.5 overflow-y-auto pr-1 content-start">
                      {isCrusherDeviceCheck(device) ? (
                        <>
                          <div className="rounded-lg border border-slate-100/80 bg-white/80 px-2 py-1 text-xs font-medium text-slate-700">
                            <p className="truncate text-slate-500">Total Crush</p>
                            <p className="mt-0.5 font-semibold tabular-nums text-emerald-700">
                              {crusherTotalsMap[device.deviceId]?.totalCrush?.toFixed(2) || 0}
                            </p>
                          </div>
                          <div className="rounded-lg border border-slate-100/80 bg-white/80 px-2 py-1 text-xs font-medium text-slate-700">
                            <p className="truncate text-slate-500">On Time (Min)</p>
                            <p className="mt-0.5 font-semibold tabular-nums text-emerald-700">
                              {crusherTotalsMap[device.deviceId]?.totalOnTimeMinutes || 0}
                            </p>
                          </div>
                        </>
                      ) : hasSensorRows ? (
                        sensorEntries.map(([key, value]) => (
                            <div
                              key={key}
                              className="rounded-lg border border-slate-100/80 bg-white/80 px-2 py-1 text-xs font-medium text-slate-700"
                            >
                              <p className="truncate capitalize text-slate-500">
                                {key}
                              </p>
                              <p className="mt-0.5 truncate font-semibold tabular-nums text-emerald-700">
                                {formatCardSensorValue(value)}
                              </p>
                            </div>
                          ))
                      ) : (
                        <p className="col-span-2 flex flex-1 items-center justify-center text-center text-sm text-slate-400">
                          Loading...
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-gray-500">
            No device data found for this project.
          </p>
        )}
      </div>

      {/* 🔹 Normal Device Metrics */}

      {showValueBoxes && selectedDevice && (
        <>
          {isCrusherDeviceCheck(selectedDevice) ? (
            // ✅ Crusher Device
            <div className="flex flex-wrap justify-start gap-4 sm:gap-5">
              <div
                onClick={() => {
                  setSelectedMetric("crusher");
                  setSelectedRange("Daily");
                  fetchCrusherSummary("today");
                }}
                className="w-full max-w-[248px] shrink-0 bg-white rounded-2xl shadow-md p-5 border border-gray-200 cursor-pointer hover:shadow-lg hover:border-[#0a5c33] transition sm:w-[248px]"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-gray-700 text-lg font-semibold capitalize">
                    Total Crush (Today)
                  </p>
                </div>

                <p className="text-[#0a5c33] mt-3 text-left text-4xl font-bold leading-tight sm:text-5xl">
                  {crusherTotalsMap[
                    selectedDevice.deviceId
                  ]?.totalCrush?.toFixed(2) || 0}{" "}
                  <span className="text-lg font-semibold text-gray-500 sm:text-xl">
                    kg
                  </span>
                </p>
              </div>

              <div
                onClick={() => {
                  setSelectedMetric("crusher");
                  setSelectedRange("Daily"); // default
                  fetchCrusherSummary("today");
                }}
                className="w-full max-w-[248px] shrink-0 bg-white rounded-2xl shadow-md p-5 border border-gray-200 cursor-pointer hover:shadow-lg hover:border-[#0a5c33] transition sm:w-[248px]"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-gray-700 text-lg font-semibold capitalize">
                    On Time (Minutes)
                  </p>
                </div>

                <p className="text-[#0a5c33] mt-3 text-left text-4xl font-bold leading-tight sm:text-5xl">
                  {crusherTotalsMap[selectedDevice.deviceId]
                    ?.totalOnTimeMinutes || 0}{" "}
                  <span className="text-lg font-semibold text-gray-500 sm:text-xl">
                    min
                  </span>
                </p>
              </div>
            </div>
          ) : (
            // ✅ Normal IoT Device
            deviceDataMap[selectedDevice.deviceId] && (
              <div className="w-full min-w-0">
                <h3 className="mb-3 text-left text-xs font-bold uppercase tracking-[0.12em] text-slate-800 sm:text-sm">
                  Sensors
                </h3>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => scrollSensors("left")}
                    aria-label="Scroll sensors left"
                    className="absolute left-0 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/90 text-emerald-700 shadow-md backdrop-blur-sm transition hover:scale-105 hover:border-emerald-200 sm:h-9 sm:w-9"
                  >
                    <ChevronLeft size={18} strokeWidth={2.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollSensors("right")}
                    aria-label="Scroll sensors right"
                    className="absolute right-0 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-white/90 text-emerald-700 shadow-md backdrop-blur-sm transition hover:scale-105 hover:border-emerald-200 sm:h-9 sm:w-9"
                  >
                    <ChevronRight size={18} strokeWidth={2.5} />
                  </button>
                  <div
                    ref={sensorScrollRef}
                    className="mx-8 overflow-x-auto overflow-y-hidden pb-2 scrollbar-hide [-webkit-overflow-scrolling:touch] sm:mx-10"
                  >
                    <div className="grid grid-rows-2 grid-flow-col auto-cols-[140px] gap-2 px-1 sm:auto-cols-[160px] sm:gap-3">
                      {Object.entries(deviceDataMap[selectedDevice.deviceId])
                        .filter(([key]) => key.toLowerCase() !== "timestamp")
                        .map(([key, value]) => (
                      <div
                        key={key}
                        onClick={() => handleMetricClick(key)}
                        className={`rounded-lg border bg-white px-2.5 py-2 shadow-sm transition hover:border-[#0a5c33] hover:shadow-md cursor-pointer sm:rounded-xl sm:px-3 sm:py-2.5 ${
                          selectedMetric === key
                            ? "border-2 border-emerald-500 shadow-md shadow-emerald-500/20"
                            : "border-slate-100/80"
                        }`}
                      >
                        <p className="text-[11px] font-semibold capitalize leading-tight text-slate-500 truncate sm:text-xs">
                          {key}
                        </p>

                        <p className="mt-1 text-left text-lg font-bold leading-tight text-emerald-700 sm:text-xl">
                          {typeof value === "number" ? value.toFixed(2) : value}
                          <span className="ml-0.5 text-[10px] font-semibold text-gray-500 sm:text-xs">
                            {key.toLowerCase() === "ph"
                              ? "pH"
                              : key.toLowerCase() === "temperature"
                                ? "°C"
                                : ""}
                          </span>
                        </p>
                      </div>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => fetchAllSensorsGraph("Daily")}
                    disabled={allSensorsLoading}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#34C759] to-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-emerald-600/20 transition hover:brightness-105 active:scale-[0.98] disabled:opacity-60"
                  >
                    <Layers size={16} />
                    {allSensorsLoading ? "Loading..." : "View All Sensors Graph"}
                  </button>
                </div>
              </div>
            )
          )}
        </>
      )}

      {/* 🔹 All Sensors Graph */}
      {showAllSensorsGraph && (
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-4 sm:p-6">
          <div className="mb-4 flex w-full min-w-0 items-center justify-between gap-2">
            <h3 className="min-w-0 flex-1 truncate text-left text-lg font-semibold text-slate-900">
              All Sensors
            </h3>
            <select
              value={allSensorsRange}
              aria-label="All sensors date range"
              className="ml-2 shrink-0 rounded-md border border-slate-300 bg-white py-1 pl-2 pr-6 text-xs text-slate-800 shadow-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 sm:text-sm sm:py-1.5 sm:pl-2.5 sm:pr-7"
              onChange={(e) => {
                setAllSensorsRange(e.target.value);
                fetchAllSensorsGraph(e.target.value);
              }}
            >
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
            </select>
          </div>

          <div className="w-full min-w-0 overflow-x-auto rounded-xl border border-slate-100 bg-slate-50/40 pb-1">
            <div className="h-[320px] min-h-[280px] sm:h-[380px]">
              {allSensorsChartData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">
                  No data for this range.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={allSensorsChartData}
                    accessibilityLayer={false}
                    margin={{ top: 8, right: 8, left: 0, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                    <XAxis
                      dataKey="fullTime"
                      type="number"
                      scale="time"
                      domain={["dataMin", "dataMax"]}
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      tickFormatter={formatChartAxisTick}
                      tickLine={false}
                      axisLine={false}
                      minTickGap={40}
                    />
                    <YAxis
                      width={40}
                      tickCount={5}
                      tick={{ fontSize: 10, fill: "#64748b", dx: -2 }}
                      tickLine={false}
                      axisLine={false}
                      domain={["auto", "auto"]}
                    />
                    <Tooltip
                      cursor={false}
                      allowEscapeViewBox={{ x: false, y: false }}
                      contentStyle={{
                        borderRadius: "12px",
                        border: "1px solid #e2e8f0",
                        fontSize: "11px",
                        maxHeight: "200px",
                        overflowY: "auto",
                      }}
                      labelFormatter={(_label, payload) =>
                        payload?.[0]?.payload?.fullDate ?? _label
                      }
                      formatter={(value, name) => [
                        typeof value === "number" ? value.toFixed(3) : value,
                        name,
                      ]}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: "11px", paddingTop: 4 }}
                      verticalAlign="top"
                      align="right"
                    />
                    {allSensorKeys.map((key, i) => (
                      <Area
                        key={key}
                        type="monotone"
                        dataKey={key}
                        name={key}
                        stroke={ALL_SENSOR_COLORS[i % ALL_SENSOR_COLORS.length]}
                        strokeWidth={1.5}
                        fill="none"
                        dot={false}
                        activeDot={{ r: 4 }}
                        connectNulls
                        isAnimationActive={false}
                      />
                    ))}
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🔹 Graph Section */}
      {showGraph && (
        <div className="bg-white rounded-2xl shadow-md border border-gray-200 p-4 sm:p-6">
          {selectedRange === "Custom" && (
            <div className="mb-5 mt-1 flex w-full min-w-0 flex-col gap-3 sm:mt-0 sm:flex-row sm:items-end sm:gap-4">
              <div className="flex w-full min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:gap-4">
                <div className="flex min-w-0 flex-1 flex-col">
                  <label
                    htmlFor="metrics-graph-custom-start"
                    className="mb-1 text-left text-sm text-slate-600"
                  >
                    Start Date
                  </label>
                  <NativeDateInput
                    id="metrics-graph-custom-start"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                </div>

                <div className="flex min-w-0 flex-1 flex-col">
                  <label
                    htmlFor="metrics-graph-custom-end"
                    className="mb-1 text-left text-sm text-slate-600"
                  >
                    End Date
                  </label>
                  <NativeDateInput
                    id="metrics-graph-custom-end"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </div>
              </div>

              <button
                type="button"
                className="w-full shrink-0 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-emerald-700 sm:w-auto sm:self-end"
                onClick={() => {
                  if (selectedMetric === "crusher") {
                    fetchCrusherSummary("custom", customStart, customEnd);
                  } else {
                    fetchGraphByRange("Custom", customStart, customEnd);
                  }
                }}
              >
                Apply
              </button>
            </div>
          )}
          <div className="mb-4 w-full min-w-0 space-y-2.5">
            <div className="flex w-full min-w-0 items-center justify-between gap-2">
              <h3 className="min-w-0 flex-1 basis-0 truncate text-left text-base font-semibold capitalize text-slate-900 sm:text-lg">
                {selectedMetric === "crusher"
                  ? "Crusher Summary"
                  : selectedMetric}
              </h3>

              <select
                value={selectedRange}
                aria-label="Graph date range"
                className="ml-2 shrink-0 rounded-md border border-slate-300 bg-white py-1 pl-2 pr-6 text-xs text-slate-800 shadow-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/25 sm:text-sm sm:py-1.5 sm:pl-2.5 sm:pr-7"
                onChange={(e) => {
                  const value = e.target.value;
                  setSelectedRange(value);

                  if (selectedMetric === "crusher") {
                    let type = "today";

                    if (value === "Weekly") type = "weekly";
                    else if (value === "Monthly") type = "monthly";
                    else if (value === "Daily") type = "today";

                    fetchCrusherSummary(type);
                    fetchCrusherTotal("today");
                  } else {
                    fetchGraphByRange(value);
                  }
                }}
              >
                <option value="Daily">Daily</option>
                <option value="Weekly">Weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Custom">Custom</option>
              </select>
            </div>

            {selectedMetric === "crusher" && (
              <div className="flex w-full flex-wrap items-center justify-between gap-x-6 gap-y-1 border-t border-slate-100 pt-2.5 sm:justify-center sm:gap-x-10 sm:pt-2">
                <div className="min-w-0 text-left sm:text-center">
                  <p className="text-xs text-gray-500">Total Crush</p>
                  <p className="text-base font-bold text-[#34C759] sm:text-lg">
                    {crusherRangeTotals.totalCrush.toFixed(2)} Kg
                  </p>
                </div>

                <div className="min-w-0 text-left sm:text-center">
                  <p className="text-xs text-gray-500">Total On Time</p>
                  <p className="text-base font-bold text-emerald-600 sm:text-lg">
                    {crusherRangeTotals.totalOnTimeMinutes} Min
                  </p>
                </div>
              </div>
            )}
          </div>

          <div
            ref={chartRef}
            className={`w-full min-w-0 overflow-y-hidden rounded-xl border border-slate-100 bg-slate-50/40 pb-1 [-webkit-tap-highlight-color:transparent] outline-none focus:outline-none focus-visible:outline-none ${
              chartHScrollEnabled ? "overflow-x-auto" : "overflow-x-hidden"
            }`}
          >
            <div
              className="h-[260px] min-h-[240px] sm:h-[300px] outline-none [-webkit-tap-highlight-color:transparent] [&_.recharts-cartesian-axis-tick_text]:outline-none [&_.recharts-layer]:outline-none [&_.recharts-responsive-container]:outline-none [&_.recharts-surface]:outline-none [&_.recharts-surface]:ring-0 [&_.recharts-surface:focus]:outline-none [&_.recharts-surface:focus-visible]:outline-none [&_.recharts-tooltip-cursor]:hidden [&_.recharts-wrapper]:outline-none [&_.recharts-wrapper]:ring-0 [&_.recharts-wrapper:focus]:outline-none [&_.recharts-wrapper:focus-visible]:outline-none [&_g]:outline-none [&_path]:outline-none [&_rect]:outline-none [&_svg]:outline-none [&_svg]:ring-0 [&_svg:focus]:outline-none [&_svg:focus-visible]:outline-none"
              style={
                chartHScrollEnabled && chartScrollWidthPx
                  ? {
                      width: `${chartScrollWidthPx}px`,
                      minWidth: `${chartScrollWidthPx}px`,
                    }
                  : { width: "100%", minWidth: 0 }
              }
            >
              {chartData.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-slate-500">
                  No data for this range.
                </div>
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={chartData}
                  accessibilityLayer={false}
                  margin={{
                    top: 8,
                    right: selectedMetric === "crusher" ? 28 : 8,
                    left: selectedMetric === "crusher" ? 4 : 0,
                    bottom: 8,
                  }}
                >
                  <defs>
                    <linearGradient id="metricFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="crushFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22c55e" stopOpacity={0.42} />
                      <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="ontimeFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#16a34a" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#16a34a" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e2e8f0"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="fullTime"
                    type="number"
                    scale="time"
                    domain={["dataMin", "dataMax"]}
                    tick={{ fontSize: 10, fill: "#64748b" }}
                    tickFormatter={formatChartAxisTick}
                    tickLine={false}
                    axisLine={false}
                    minTickGap={40}
                  />
                  <YAxis
                    yAxisId="left"
                    width={selectedMetric === "crusher" ? 40 : 32}
                    tickCount={5}
                    tick={{ fontSize: 10, fill: "#64748b", dx: -2 }}
                    tickLine={false}
                    axisLine={false}
                    domain={["auto", "auto"]}
                    tickFormatter={(v) =>
                      typeof v === "number" && Number.isFinite(v)
                        ? String(Number(v.toFixed(2)))
                        : String(v ?? "")
                    }
                  />
                  {selectedMetric === "crusher" && (
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      width={36}
                      tickCount={5}
                      tick={{ fontSize: 10, fill: "#64748b" }}
                      tickLine={false}
                      axisLine={false}
                      domain={["auto", "auto"]}
                      tickFormatter={(v) =>
                        typeof v === "number" && Number.isFinite(v)
                          ? String(Number(v.toFixed(1)))
                          : String(v ?? "")
                      }
                    />
                  )}
                  <Tooltip
                    cursor={false}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ outline: "none", boxShadow: "none", zIndex: 20 }}
                    contentStyle={{
                      borderRadius: "12px",
                      border: "1px solid #e2e8f0",
                      fontSize: "12px",
                      outline: "none",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.07)",
                    }}
                    labelFormatter={(_label, payload) => {
                      if (payload?.length > 0) {
                        return payload[0].payload.fullDate;
                      }
                      return _label;
                    }}
                    formatter={(value, name) => {
                      if (
                        selectedMetric === "crusher" &&
                        name === "Total Crush"
                      ) {
                        return [Number(value).toFixed(2), name];
                      }
                      if (
                        selectedMetric === "crusher" &&
                        name === "On time (min)"
                      ) {
                        return [Number(value).toFixed(1), name];
                      }
                      return [value, name];
                    }}
                  />
                  {selectedMetric === "crusher" && (
                    <Legend
                      wrapperStyle={{ fontSize: "12px", paddingTop: 4 }}
                      verticalAlign="top"
                      align="right"
                    />
                  )}

                  {selectedMetric === "crusher" ? (
                    <>
                      <Area
                        yAxisId="left"
                        type="monotone"
                        dataKey="totalCrush"
                        name="Total Crush"
                        stroke="#16a34a"
                        strokeWidth={2}
                        fill="url(#crushFill)"
                        dot={{ r: chartDotR, fill: "#15803d" }}
                        activeDot={{
                          r: 5,
                          stroke: "none",
                          strokeWidth: 0,
                          fill: "#15803d",
                        }}
                        isAnimationActive={false}
                      />
                      <Area
                        yAxisId="right"
                        type="monotone"
                        dataKey="totalOntime"
                        name="On time (min)"
                        stroke="#15803d"
                        strokeWidth={2}
                        fill="url(#ontimeFill)"
                        dot={{ r: chartDotR, fill: "#166534" }}
                        activeDot={{
                          r: 5,
                          stroke: "none",
                          strokeWidth: 0,
                          fill: "#166534",
                        }}
                        isAnimationActive={false}
                      />
                    </>
                  ) : (
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="value"
                      name={String(selectedMetric ?? "value")}
                      stroke="#15803d"
                      strokeWidth={2}
                      fill="url(#metricFill)"
                      dot={{ r: chartDotR, fill: "#166534" }}
                      activeDot={{
                        r: 5,
                        stroke: "none",
                        strokeWidth: 0,
                        fill: "#166534",
                      }}
                      connectNulls={false}
                      isAnimationActive={false}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
              )}
            </div>
          </div>

          {true && (
            <div className="text-center mt-4">
              <button
                onClick={() => setShowDownloadPopup(true)}
                className="bg-emerald-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-emerald-600 transition"
              >
                Download Report
              </button>
            </div>
          )}
        </div>
      )}

      {/* ⭐ DOWNLOAD REPORT POPUP ⭐ */}
      {showDownloadPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 px-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white/95 p-5 shadow-2xl shadow-slate-900/15 sm:p-6">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">
                  Download Report
                </h3>
                <p className="mt-1 truncate text-sm text-slate-500">
                  {selectedDevice?.name || "Device"}
                </p>
              </div>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                {selectedMetric || "Sensor"}
              </span>
            </div>

            <div className="mb-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                Range
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {["Today", "Weekly", "Monthly", "Custom"].map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                      rangeType === r
                        ? "border-emerald-500 bg-emerald-500 text-white shadow-sm shadow-emerald-500/25"
                        : "border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100"
                    }`}
                    onClick={() => setRangeType(r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            {rangeType === "Custom" && (
              <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50/80 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Custom Date Range
                </p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <NativeDateInput
                    id="metrics-pdf-start"
                    aria-label="Report start date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                  <NativeDateInput
                    id="metrics-pdf-end"
                    aria-label="Report end date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2.5">
              <button
                type="button"
                className="flex-1 rounded-lg bg-emerald-500 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-600"
                onClick={() => {
                  setShowDownloadPopup(false);

                  if (selectedMetric === "crusher") {
                    handleCrusherDownloadPDF();
                  } else {
                    handleDownloadPDF();
                  }
                }}
              >
                Download Report
              </button>
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                onClick={() => setShowDownloadPopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MetricsPage;
